// src/encounter/lib/to-chart-snapshot.ts
//
// Resolves a chart piece (spec §4.3) — `vitals.latest`, `allergies.list`, and the lab queries
// (`labs.set:<id>`, `labs.latest:<LOINC>`, see `to-chart-labs.ts`) — from reads ALREADY made,
// synchronously, so an insertion is one transaction and nothing can land after an undo.
//
//   allergies.list ← GetPatientSummary through `patientSummaryToIpsSections`, the SAME mapper the
//                    chart rail draws (same de-duplication, same labels).
//   problems.active, medications.active ← the same summary and mapper (its Active Problems and
//                    Medications sections). A medication the rail shows as Stopped is not active.
//   vitals.latest  ← GetVitalsDetailsByEncounter through `patientVitalsToRow`, the SAME mapper the
//                    vitals strip draws (derived BMI / waist:hip, the backend's abnormal flag).
//   labs.*         ← the chart Labs tab's reads (lab desk list + each request's Reports), supplied
//                    as `labs` only while `phrasePieces` is on; absent, a lab link stays "not loaded".
//   CARD FIELDS    — while `phrasePieces` is on the editor also supplies `vitalsHistory` (the
//                    patient-wide GetVitalsList the appointment Vitals tab draws its sparklines from).
//                    Then each vital line also carries `label`, `kind`, the backend's own `flag` and
//                    its `history`, for the value card. Absent (switch off), every vital line is
//                    byte-identical to before.
//
// HONEST STATES (R6). `ok` has results; `empty` = the read SUCCEEDED and returned an EXPLICIT empty
// list; `error` = the read failed, or came back PARTIAL; `loading` = it has not returned. An error or a
// pending read is never an empty list.
// A summary that OMITS a list field (absent, `null`, not a list) is a partial read: the chart link
// says it could not be loaded, never "No allergies recorded" — and never "No known allergies". That is
// the same test the chart rail makes (`unavailableSummarySections`, PROD-2804), so the note and the
// rail cannot disagree about one response. Every summary-backed chart link goes through
// `resolveSummaryList`, so a future medications or problems link inherits the rule; the vitals read
// must likewise be a list to count as read.
//
// The query is kept on the snapshot, so Refresh re-runs the QUERY against the page's current data
// (new records included), never the old ids.

import type { VitalReading, VitalsRowVitals } from '@scripsteam/scrips-signal-ds';
import type { PatientSummary } from '@/types/patient-summary-api';
import { VITAL_CODES, type PatientVital } from '@/types/vitals-api';
import {
  allergyCriticality,
  allergyName,
  patientSummaryToIpsSections,
  railAllergies,
  unavailableSummarySections,
  type SUMMARY_FIELD_BY_SECTION,
} from './to-ips-sections';
import type { PatientAllergy } from '@/types/patient-summary-api';
import { patientVitalsToRow } from './to-vitals-row';
import { extractVitalSeries } from '@/scheduling/components/view-appointment-panel/vitals/vital-sparkline';
import { isLabQuery, wallClockNow, type ChartQuery, type ChartResult, type ChartSnapshot } from './note-pieces';
import { resolveLabQuery, type NoteLabReads } from './to-chart-labs';

export interface ChartRead<T> {
  status: 'loading' | 'error' | 'ok';
  data?: T;
}

export interface ChartReads {
  patientId?: string;
  encounterId?: string;
  summary: ChartRead<PatientSummary>;
  vitals: ChartRead<PatientVital[]>;
  /** The chart Labs tab's reads (`to-chart-labs.ts`). Absent → lab links stay "not loaded yet". */
  labs?: ChartRead<NoteLabReads>;
  /** Patient-wide vitals history (GetVitalsList). Present only while `phrasePieces` is on → card fields. */
  vitalsHistory?: ChartRead<PatientVital[]>;
  /**
   * A FRESH read (R17): re-fetch the summary and the encounter vitals for this page's patient and
   * encounter and return the result. Absent (stories, tests) → Refresh re-reads the page data.
   */
  refetch?: () => Promise<ChartReads>;
}

/** A react-query result → a read state. A disabled or pending query is `loading`, never empty. */
export function toChartRead<T>(q: { data?: T; isError?: boolean }): ChartRead<T> {
  if (q.isError) return { status: 'error' };
  return q.data === undefined ? { status: 'loading' } : { status: 'ok', data: q.data };
}

/** Tile order + labels = the DS vitals strip; the code is the reading's own record (provenance). */
const VITALS: Array<[keyof VitalsRowVitals, string, string | undefined]> = [
  ['temp', 'Temp', VITAL_CODES.TEMPERATURE],
  ['pulse', 'Pulse', VITAL_CODES.HEART_RATE],
  ['bp', 'BP', VITAL_CODES.SYSTOLIC],
  ['respRate', 'Resp Rate', VITAL_CODES.RESP_RATE],
  ['o2Sat', 'O₂ Sat', VITAL_CODES.O2_SAT],
  ['height', 'Height', VITAL_CODES.HEIGHT],
  ['weight', 'Weight', VITAL_CODES.WEIGHT],
  ['bmi', 'BMI', VITAL_CODES.BMI],
  ['hipCirc', 'Hip Circ.', VITAL_CODES.HIP],
  ['waistCirc', 'Waist Circ.', VITAL_CODES.WAIST],
  ['waistToHipRatio', 'Waist:Hip', VITAL_CODES.WAIST_TO_HIP_RATIO],
  ['bioage', 'Bio Age', VITAL_CODES.BIOAGE],
  ['visualAcuity', 'Visual Acuity', VITAL_CODES.VISUAL],
  ['hearingTest', 'Hearing', VITAL_CODES.HEARING],
  ['headCirc', 'Head Circ.', VITAL_CODES.HCM], // 11947-9 only, as Flutter (PROD-3185)
  ['lmp', 'LMP', VITAL_CODES.LMP],
];

/** What a vital line carries for the value card: its name, its glyph key, the backend's own flag and
 *  the numeric readings on file (oldest first). Only when the editor supplied the history read. */
type VitalCard = { key: string; code: string | undefined; history: ChartRead<PatientVital[]> };

function vitalResult(label: string, r: VitalReading | undefined, source: PatientVital | undefined, card?: VitalCard): ChartResult | undefined {
  const value = typeof r?.value === 'string' || typeof r?.value === 'number' ? String(r.value) : '';
  if (!value) return undefined;
  const unit = r?.unit ?? undefined;
  // The backend's own classification, carried verbatim — never a threshold computed here.
  const abnormal = r?.semantic === 'warning';
  const series =
    card?.code && card.history.status === 'ok' && Array.isArray(card.history.data) ? extractVitalSeries(card.code, card.history.data) : [];
  return {
    sourceId: source?.identifire ?? undefined,
    display: `${label} ${value}${unit ? ` ${unit}` : ''}${abnormal ? ' (abnormal)' : ''}`,
    value,
    unit,
    measuredAt: source?.effectiveDateTime ?? source?.createdDate ?? undefined,
    ...(card ? { label, kind: card.key, ...(abnormal ? { flag: 'abnormal' } : {}), ...(series.length ? { history: series } : {}) } : {}),
  };
}

function resolveVitals(vitals: PatientVital[], history?: ChartRead<PatientVital[]>): Pick<ChartSnapshot, 'status' | 'results'> {
  const live = vitals.filter((v) => !v.isDeleted);
  const byCode = new Map<string, PatientVital>();
  for (const v of live) {
    const code = v.code?.code ?? v.quantity?.code;
    if (code) byCode.set(code, v);
  }
  const row = patientVitalsToRow(live);
  const results = VITALS.map(([key, label, code]) =>
    vitalResult(label, row[key], code ? byCode.get(code) : undefined, history ? { key, code, history } : undefined),
  ).filter(
    (r): r is ChartResult => Boolean(r),
  );
  return { status: results.length ? 'ok' : 'empty', results };
}

/**
 * One allergy as a note quotes it (team rule: one source per fact). The SAME list the chart rail shows,
 * in its order (`railAllergies`), in the rail's words: the substance, then each recorded fact WITH ITS
 * LABEL — "Penicillin (high criticality)" (design desk advisory: reads more naturally to a clinician). A bare "High" could read as a severity and contradict a
 * reaction recorded elsewhere, so criticality is always named. The summary carries no reaction or
 * severity; when it does, they belong here, labelled the same way.
 */
export function allergyNoteText(a: PatientAllergy): string {
  const criticality = allergyCriticality(a);
  return criticality ? `${allergyName(a)} (${criticality.toLowerCase()} criticality)` : allergyName(a);
}

/**
 * A list read from the patient summary (R6). The field must be DELIVERED as a list: omitted, `null` or
 * not a list is a partial read (`error`), so "none recorded" can only ever come from an explicit `[]`.
 */
function resolveSummaryList(
  summary: PatientSummary,
  section: keyof typeof SUMMARY_FIELD_BY_SECTION,
): Pick<ChartSnapshot, 'status' | 'results'> {
  if (unavailableSummarySections(summary).includes(section)) return { status: 'error', results: [] };
  // Allergies: the rail's own list, in its order and words, criticality labelled (#611's resolver).
  if (section === 'allergies') {
    const results = railAllergies(summary).map((a, i) => ({ sourceId: a.identifier || `allergy-${i}`, display: allergyNoteText(a) }));
    return { status: results.length ? 'ok' : 'empty', results };
  }
  // An active-medications quote leaves out a medication whose STATUS is stop (the status the rail
  // reads to show it as Stopped) — filtered on the record's status before the rail's mapper runs,
  // never on how the rail happens to draw it (Codex review).
  const source = section === 'medications' ? { ...summary, medication: (summary.medication ?? []).filter((m) => !isStopped(m.status)) } : summary;
  const found = patientSummaryToIpsSections(source).find((s) => s.id === section);
  const results: ChartResult[] = (found?.entries ?? []).map((e) => ({
    sourceId: e.id,
    display: e.secondary ? `${e.primary} — ${e.secondary}` : e.primary,
  }));
  return { status: results.length ? 'ok' : 'empty', results };
}

/** The summary's stopped status, read the way the rail's mapper reads it ("stop", any case). */
function isStopped(status: string | null | undefined): boolean {
  return (status ?? '').trim().toLowerCase() === 'stop';
}

/** Which chart-rail section each summary-backed query quotes. */
const SUMMARY_SECTION: Partial<Record<ChartQuery, keyof typeof SUMMARY_FIELD_BY_SECTION>> = {
  'allergies.list': 'allergies',
  'problems.active': 'active-problems',
  'medications.active': 'medications',
};

export function resolveChart(query: ChartQuery, reads: ChartReads | undefined, now: Date = new Date()): ChartSnapshot {
  const base = { query, patientId: reads?.patientId, encounterId: reads?.encounterId, pulledAt: wallClockNow(now) };
  // G5: a quote is stamped with the patient AND visit it was read for, which is how a reopened note
  // tells its own values from a pasted one. Before both are known nothing is quoted: the link stays
  // "not loaded yet" (no value), never a value that a later open could not place.
  if (!reads?.patientId || !reads?.encounterId) return { ...base, status: 'loading', results: [] };
  if (isLabQuery(query)) return { ...base, ...resolveLabQuery(query, reads.patientId, reads.labs) };
  const read = query === 'vitals.latest' ? reads?.vitals : reads?.summary;
  if (!read || read.status === 'loading') return { ...base, status: 'loading', results: [] };
  if (read.status === 'error' || read.data == null) return { ...base, status: 'error', results: [] };
  if (query === 'vitals.latest') {
    // R6: a vitals body that is not a list was not read — never "No vitals recorded".
    if (!Array.isArray(read.data)) return { ...base, status: 'error', results: [] };
    return { ...base, ...resolveVitals(read.data as PatientVital[], reads.vitalsHistory) };
  }
  const section = SUMMARY_SECTION[query];
  // A query no reader answers is never quoted from another list.
  if (!section) return { ...base, status: 'error', results: [] };
  return { ...base, ...resolveSummaryList(read.data as PatientSummary, section) };
}

/**
 * Same content = same state and the same quoted lines FROM THE SAME RECORDS (the pull time is not
 * content). Each line's source record and measurement time count: a newer reading that happens to
 * show the same value is a different fact — the note's "measured …" and its provenance must move to
 * it — so Refresh offers Update rather than calling it unchanged.
 */
export function sameChartContent(a: ChartSnapshot, b: ChartSnapshot): boolean {
  const lines = (s: ChartSnapshot) => JSON.stringify(s.results.map((r) => [r.display, r.sourceId ?? null, r.measuredAt ?? null]));
  return a.status === b.status && lines(a) === lines(b);
}
